import React, { useRef, useState, useEffect, useContext, PropsWithChildren } from 'react'
import './data-table-view.scss'
import classnames from 'classnames'
import { FilterFilled, Loading3QuartersOutlined } from '@ant-design/icons'
import { Input, Select } from 'antd'
import { SelectProps } from 'antd/lib/select'
import { Well } from '@pushly/aqe/lib/components'
import * as deepEqual from 'react-fast-compare'
import { resolveCustomFiltersProp } from '@halo/components/data-table-view/helpers'
import { PromiseableProp, resolvePromiseableProp } from '@halo/helpers/promiseable-prop'
import { useLoadableDataState } from '@halo/hooks/use-loadable-data-state'
import { DataSourceResolver } from './types'
import { DataTableContext } from './context'

type FiltersValue = { tableSearchValue?: string } & { [dataIndex: string]: any }

interface IDataTableFilterOption {
    group?: string
    value: any
    label: string
    title?: string
    disabled?: boolean
    isDisabled?: (option: IDataTableFilterOption, selections: any[] | null) => boolean
}

interface IDataTableFilter extends SelectProps<any> {
    dataIndex: string // use dot notation
    multiple?: boolean
    optionPrefix?: string
    options: IDataTableFilterOption[]
    onChange?: (...args: any[]) => void
}

const SimpleFilterWrapper: React.FunctionComponent<any> = ({ className, children }) => (
    <div className={className}>{children}</div>
)

const SelectFilter = (props: any) => {
    const focused = useRef(false)
    const { filter, multiple, customFilters, onChange, defaultValue, ...selectProps } = props
    const [selectValues, setSelectValues] = useState<any>(props.defaultValue)

    const groups: any = {}
    filter.options.forEach((opt: IDataTableFilterOption) => {
        groups[opt.group ?? '__default__'] = [...(groups[opt.group ?? '__default__'] ?? []), opt]
    })
    const useGroups = Object.keys(groups).length > 1

    return (
        <Select
            {...selectProps}
            className={classnames('data-table-filter-select')}
            dropdownClassName={classnames('data-table-filter-select-overlay', selectProps.dropdownClassName)}
            size="small"
            mode={!filter.multiple ? undefined : 'multiple'}
            defaultValue={defaultValue}
            onChange={(v: any[], e: any) => {
                setSelectValues(v)
                /**
                 * only handle non-focused changes
                 * if mode === multiple
                 * eg: clicking remove icon
                 */
                if (!multiple || !focused.current) {
                    onChange(filter, v)
                }
            }}
            onFocus={() => {
                focused.current = true
            }}
            onBlur={
                !multiple
                    ? undefined
                    : (v: any) => {
                          const hasChanged = !deepEqual(customFilters[filter.dataIndex] ?? [], selectValues)
                          if (hasChanged) {
                              onChange(filter, selectValues)
                          }

                          focused.current = false
                      }
            }
            onDropdownVisibleChange={(visible) => {
                /**
                 * backup focus/blur handler
                 * used to capture hidden antd input click
                 * which does not trigger onFocus
                 */
                if (visible && !focused.current) {
                    focused.current = true
                } else if (!visible && focused.current) {
                    focused.current = false
                }
            }}
        >
            {!useGroups
                ? filter.options.map((filterOpt: any) => (
                      <Select.Option
                          key={filterOpt.value}
                          value={filterOpt.value}
                          title={filterOpt.title}
                          disabled={
                              filterOpt.disabled ?? filterOpt.isDisabled?.(filterOpt, customFilters[filter.dataIndex])
                          }
                      >
                          {filterOpt.label ?? filterOpt.value}
                      </Select.Option>
                  ))
                : Object.keys(groups).map((groupKey: any) => (
                      <Select.OptGroup key={groupKey} label={groupKey === '__default__' ? 'General' : groupKey}>
                          {groups[groupKey].map((filterOpt: any) => (
                              <Select.Option
                                  key={filterOpt.value}
                                  value={filterOpt.value}
                                  title={filterOpt.title}
                                  disabled={
                                      filterOpt.disabled ??
                                      filterOpt.isDisabled?.(filterOpt, customFilters[filter.dataIndex])
                                  }
                              >
                                  {filterOpt.label ?? filterOpt.value}
                              </Select.Option>
                          ))}
                      </Select.OptGroup>
                  ))}
        </Select>
    )
}

interface IDataTableFilters<T extends any> {
    hideSearch?: boolean
    searchPlaceholder?: string
    onSearchChange?: (value: string) => void
    onSearchBtnClick?: (value: string) => void
    onFiltersChange?: (filters: FiltersValue) => void
    filters?: PromiseableProp<IDataTableFilter[]>
    filtersDefaultValue?: FiltersValue
    filtersOverride?: React.ReactNode
}

const DataTableFilters = <T extends any>(props: IDataTableFilters<T>) => {
    const {
        hideSearch,
        searchPlaceholder,
        onSearchChange,
        onFiltersChange,
        filters,
        filtersDefaultValue,
        filtersOverride,
    } = props

    const context = useContext(DataTableContext)
    const [filterOpts, setFilterOpts] = useLoadableDataState<any[]>({ data: [] })
    const [customFilters, setCustomFilters] = useState<any>(
        resolveCustomFiltersProp(filterOpts.data, filtersDefaultValue),
    )
    let searchDebounce: any

    /**
     * Load Filters
     *
     * dep [!!filters] ensures a single load
     * when no longer undefined
     */
    useEffect(() => {
        const loadFilterOpts = async () => {
            setFilterOpts({ loading: true })
            const loadedFilters = await resolvePromiseableProp(filters)
            setFilterOpts({ loading: false, data: loadedFilters })
            setCustomFilters(resolveCustomFiltersProp(loadedFilters, filtersDefaultValue))
        }
        loadFilterOpts()
    }, [!!filters])

    const handleFilterChange = async (filter: IDataTableFilter, ...args: any[]) => {
        customFilters[filter.dataIndex] = Array.isArray(args[0]) ? args[0] : []
        setCustomFilters(customFilters)

        // pass undefined in pagination slot to inherit
        await context.resolveDataSource?.(undefined, customFilters)

        filter.onChange?.(...args)
        onFiltersChange?.(customFilters)
    }

    const handleSearchChange = async (value: string, emitter?: (v: string) => void) => {
        customFilters.tableSearchValue = value
        setCustomFilters(customFilters)

        // pass undefined in pagination slot to inherit
        await context.resolveDataSource?.(undefined, customFilters)

        emitter?.(value)
        onFiltersChange?.(customFilters)
    }

    const hasFiltersOverride = !!filtersOverride

    return (
        <>
            <div className={classnames('data-table-search')}>
                {!hideSearch && (
                    <Input.Search
                        size="large"
                        placeholder={searchPlaceholder ?? 'Search...'}
                        defaultValue={filtersDefaultValue?.tableSearchValue}
                        allowClear={true}
                        onSearch={(v) => {
                            clearTimeout(searchDebounce)
                            handleSearchChange(v, onSearchChange)
                        }}
                        onChange={(ev) => {
                            const v = ev.target.value

                            /**
                             * Ignoring 'click' change events to
                             *  prevent duplicate fetch calls resulting
                             *  in cross cancellation.
                             *
                             * Clearing the input via the clear btn
                             *  triggers onChange & onSearch.
                             */
                            if (ev.type !== 'click') {
                                clearTimeout(searchDebounce)
                                searchDebounce = setTimeout(() => handleSearchChange(v, onSearchChange), 520)
                            }
                        }}
                    />
                )}
            </div>
            <div className={classnames('data-table-filters')}>
                {hideSearch && filterOpts.data?.length && <FilterFilled className="data-table-filter-icon" />}

                {filterOpts.loading && <Loading3QuartersOutlined className="data-table-filter-icon" spin={true} />}

                {hasFiltersOverride && <React.Fragment>{filtersOverride}</React.Fragment>}

                {!hasFiltersOverride &&
                    filterOpts.data?.map((filter, filterIdx) => {
                        const { className, dataIndex, optionPrefix, options, onChange, ...selectProps } = filter
                        const hasPrefix = !!optionPrefix
                        const FilterWrapper = hasPrefix ? Input.Group : SimpleFilterWrapper
                        const defaultValue = filtersDefaultValue?.[filter.dataIndex]

                        return (
                            <FilterWrapper
                                key={`filter${filterIdx}`}
                                className={classnames('data-table-filter', filter.className)}
                                size="small"
                                compact={true}
                            >
                                {hasPrefix && (
                                    <label className={classnames('data-table-filter-prefix-label')}>
                                        {filter.optionPrefix}:
                                    </label>
                                )}
                                <SelectFilter
                                    {...selectProps}
                                    filter={filter}
                                    customFilters={customFilters}
                                    defaultValue={defaultValue}
                                    onChange={handleFilterChange}
                                />
                            </FilterWrapper>
                        )
                    })}
            </div>
        </>
    )
}

interface IDataTableView<T extends any> {
    hideSearch?: boolean
    searchPlaceholder?: string
    onSearchChange?: (value: string) => void
    onSearchBtnClick?: (value: string) => void
    onFiltersChange?: (filters: FiltersValue) => void
    filters?: PromiseableProp<IDataTableFilter[]>
    filtersDefaultValue?: FiltersValue
    actions?: React.ReactNode
    header?: React.ReactNode
    filtersOverride?: React.ReactNode
}

const DataTableView: React.FC<PropsWithChildren<IDataTableView<any>>> = (props) => {
    const { hideSearch, filters, filtersOverride, actions, header, children } = props

    const actionsAreCallable = typeof actions === 'function'
    const hasFilters = (Array.isArray(filters) && filters.length > 0) || !!filtersOverride
    const hasActions = actionsAreCallable || (Array.isArray(actions) && actions.length > 0)
    const hasHeader = !!header
    const showHeaderUpper = hasHeader
    const showHeaderLower = !hideSearch || hasFilters || hasActions
    const showHeader = showHeaderUpper || showHeaderLower

    return (
        <Well
            className={classnames('data-table-view', 'type-3 type-2')}
            hideFooter={true}
            hideHeader={!showHeader}
            header={
                !showHeader ? undefined : (
                    <div className={classnames('data-table-header')}>
                        {showHeaderUpper && (
                            <div className={classnames('data-table-header-upper')}>
                                <div className={classnames('data-table-header-left')}>
                                    {hasHeader && <div className={classnames('data-table-header-title')}>{header}</div>}
                                </div>
                            </div>
                        )}

                        {showHeaderLower && (
                            <div className={classnames('data-table-header-lower')}>
                                <div className={classnames('data-table-header-left')}>
                                    <DataTableFilters
                                        hideSearch={props.hideSearch}
                                        searchPlaceholder={props.searchPlaceholder}
                                        onSearchChange={props.onSearchChange}
                                        onSearchBtnClick={props.onSearchBtnClick}
                                        onFiltersChange={props.onFiltersChange}
                                        filters={props.filters}
                                        filtersDefaultValue={props.filtersDefaultValue}
                                        filtersOverride={props.filtersOverride}
                                    />
                                </div>
                                <div className={classnames('data-table-header-right')}>
                                    <div className={classnames('data-table-actions')}>{actions}</div>
                                </div>
                            </div>
                        )}
                    </div>
                )
            }
        >
            {children}
        </Well>
    )
}

export default DataTableView
